/**
 * The live-SSE subscription core (framework-agnostic, unit-testable). Wraps a
 * browser `EventSource` (or any injected `EventSourceLike` — the seam the tests
 * drive with a fake) onto the typed `StreamEvent` contract.
 *
 * Graceful reconnect is NATIVE: an `EventSource` auto-reconnects on drop and
 * replays via `Last-Event-ID`, which the backend honours (replay-on-connect).
 * We surface the connection status so the UI can show a subtle "reconnecting…"
 * hint, and we register one typed listener per `STREAM_EVENTS` name so the
 * browser demultiplexes the frames for us.
 */

import { type StreamEvent, STREAM_EVENTS, parseStreamEvent } from "./events";

export type StreamStatus = "connecting" | "open" | "reconnecting" | "closed";

/** The subset of `EventSource` we use — the injectable seam for tests. */
export interface MessageEventLike {
  data: string;
  lastEventId?: string;
}
export interface EventSourceLike {
  addEventListener(
    type: string,
    listener: (ev: MessageEventLike) => void,
  ): void;
  close(): void;
  onopen: ((ev?: unknown) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

/** Default factory: the real single-origin `EventSource` (cookie auth rides it). */
export const defaultEventSourceFactory: EventSourceFactory = (url) =>
  new EventSource(url, { withCredentials: true }) as unknown as EventSourceLike;

/** The stream URL for a workspace (single origin, cookie auth). */
export function streamUrl(slug: string): string {
  return `/api/workspaces/${encodeURIComponent(slug)}/stream`;
}

export interface StreamHandlers {
  onEvent: (event: StreamEvent) => void;
  onStatus?: (status: StreamStatus) => void;
}

export interface Subscription {
  close(): void;
}

/**
 * Open a live subscription to a workspace's review stream. Returns a handle
 * whose `close()` tears the `EventSource` down (idempotent). A malformed frame
 * is dropped silently (parse → null); it never reaches `onEvent` and never
 * throws.
 */
export function subscribeWorkspaceStream(
  slug: string,
  handlers: StreamHandlers,
  factory: EventSourceFactory = defaultEventSourceFactory,
): Subscription {
  const { onEvent, onStatus } = handlers;
  onStatus?.("connecting");

  const es = factory(streamUrl(slug));

  es.onopen = () => onStatus?.("open");
  // EventSource fires onerror on a dropped connection while it auto-reconnects;
  // surface it as "reconnecting" rather than a terminal error.
  es.onerror = () => onStatus?.("reconnecting");

  for (const name of STREAM_EVENTS) {
    es.addEventListener(name, (ev) => {
      const parsed = parseStreamEvent(name, ev.data);
      if (parsed) onEvent(parsed);
    });
  }

  let closed = false;
  return {
    close() {
      if (closed) return;
      closed = true;
      es.close();
      onStatus?.("closed");
    },
  };
}
